import React, { useEffect, useState, useRef } from "react";
import { useQuery } from "react-query";
import Swal from "sweetalert2";
import { profileImageURL } from "../../helpers/helpers";
import { useDispatch, useSelector } from "react-redux";
import { ApiCall } from "../../Services/apis";
import logo1 from "../../assets/AmericanClinic Online.svg";
import { useNavigate } from "react-router-dom";
import DrawerApp from "../../components/DrawerApp/DrawerApp";
import ChatDrawer from "./ChatDrawer";
import ChatBox from "./ChatBox";
import { useMediaQuery } from "react-responsive";
import ReportModal from "./ReportModal";
import PrimaryButton from "../../components/UI/PrimaryButton";
import socketConnection from "../messaging/socketConnection";
import ZoomVideo from "@zoom/videosdk";
import { setLoader } from "../../Redux/Actions/GeneralActions";

const Room = ({
  roomName,
  room,
  zoomClient,
  handleLogout,
  userId,
  roomId,
  initialVideoEnabled,
  initialAudioEnabled,
  bothJoined,
  setBothJoined,
  doctorCompleted,
  setDoctorCompleted,
}) => {
  const isMobile = useMediaQuery({ query: "(max-width: 800px)" });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth.userToken);

  const [participants, setParticipants] = useState([]);
  const [users, setUsers] = useState([]);
  const [join, setJoin] = useState(false);
  const [isCamera, setIsCamera] = useState("");
  const [isAudio, setIsAudio] = useState("");
  const [bookingDetail, setBookingDetail] = useState();
  const [timePassed, setTimePassed] = useState(new Date());
  const [timePassedForDelay, setTimePassedForDelay] = useState(new Date());
  const [showReportModal, setShowReportModal] = useState(false);
  const [participantAudioState, setParticipantAudioState] = useState({});
  const [isDoctorMuted, setIsDoctorMuted] = useState(true);

  const [ringTime, setRingTime] = useState(new Date());
  const [delayTime, setDelayTime] = useState(null); // Set 24 hours in future initially
  const [countdown, setCountdown] = useState({ minutes: 0, seconds: 0 });
  const [timerMessage, setTimerMessage] = useState("");
  const [timerType, setTimerType] = useState(""); // "ring", "delay", "remind"

  // Helper function to format time
  const formatCountdown = (minutes, seconds) => {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Update the initial state values
  const [isVideo, setIsVideo] = useState(initialVideoEnabled);
  const [isMute, setIsMute] = useState(initialAudioEnabled);
  const [showCaptions, setShowCaptions] = useState(true);
  const isMuteRef = useRef(initialAudioEnabled);

  // Keep ref in sync with state so event listeners always see current value
  useEffect(() => {
    isMuteRef.current = isMute;
  }, [isMute]);

  // Deepgram & Transcripts
  const [captionHistory, setCaptionHistory] = useState([]);
  const captionEndRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const captionTimeoutRef = useRef(null);
  const transcriptLogRef = useRef([]); // To store the full conversation

  const hasInitializedRef = useRef(false);

  // Initialize transcript from backend on mount
  useEffect(() => {
    if (bookingDetail && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (bookingDetail.consultation_summary) {
        try {
          const parsed = JSON.parse(bookingDetail.consultation_summary);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Prepend database history to any new local history
            transcriptLogRef.current = [...parsed, ...transcriptLogRef.current];
            setCaptionHistory(
              transcriptLogRef.current.map((item) => ({
                speaker: item.speaker === "Patient" || item.speaker === "patient" ? "Patient" : "Doctor",
                text: item.text,
                timestamp: item.timestamp,
              }))
            );
          }
        } catch (e) {
          console.error("Failed to parse existing transcript", e);
        }
      }
    }
  }, [bookingDetail]);

  const isSyncingRef = useRef(false);
  const pendingSyncRef = useRef(false);

  const syncTranscriptToBackend = async (transcriptArray) => {
    if (!bookingDetail?._id || transcriptArray.length === 0) return;
    
    if (isSyncingRef.current) {
      pendingSyncRef.current = true;
      return;
    }

    isSyncingRef.current = true;
    try {
      await ApiCall({
        route: "booking/save_consultation_summary",
        token: token,
        verb: "post",
        params: {
          booking_id: bookingDetail._id,
          transcript: transcriptArray,
        },
      });
    } catch (err) {
      console.error("Error syncing transcript:", err);
    } finally {
      isSyncingRef.current = false;
      if (pendingSyncRef.current) {
        pendingSyncRef.current = false;
        syncTranscriptToBackend(transcriptLogRef.current);
      }
    }
  };

  // console.log("---join---", join);
  // console.log("---participants---", participants);

  // const zoomClient = useRef(null);
  // Removed shadowed zoomClient to use the prop instead
  // const zoomClient = ZoomVideo.createClient();

  // console.log(
  //   "Video show status ",
  //   isCamera,
  //   isVideo,
  //   "Microphone show status",
  //   isAudio,
  //   isMute
  // );
  const getBookingDetails = async () => {
    if (!roomName || roomName === "undefined") {
      console.log("No room name provided for booking details");
      return null;
    }
    try {
      const res = await ApiCall({
        route: `booking/get_booking_detail/${roomName}`,
        token: token,
        verb: "get",
      });
      if (res?.status === 200) {
        // return setBookingDetail(res?.response?.booking_detail);
        const bookingDetail = res?.response?.booking_detail;
        setBookingDetail(bookingDetail);
        return bookingDetail;
      } else {
        console.log("your error is there", res?.response);
      }
    } catch (e) {
      console.log("your error is there", e);
    }
  };

  const { isLoading, data } = useQuery("bookingDetails", getBookingDetails);

  // Auto-scroll caption history
  useEffect(() => {
    if (captionEndRef.current) {
      captionEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [captionHistory]);

  useEffect(() => {
    const processUserAdded = (user) => {
      const currentUser = zoomClient.getCurrentUserInfo();
      if (!user || (currentUser && user.userId === currentUser.userId)) return;

      // Robust identification: In this clinic room context, anyone else is the doctor/provider
      const isDoctor =
        (bookingDetail?.doctor_id?.user_id &&
          String(user.customId) === String(bookingDetail.doctor_id.user_id)) ||
        (!bookingDetail?.doctor_id?.user_id &&
          user.displayName &&
          user.displayName.trim() !== String(userId).trim() &&
          user.customId !== String(userId)) ||
        // Final fallback: if someone joined and it's not us, acknowledge their presence
        user.userId !== currentUser?.userId;

      if (isDoctor) {
        console.log("Doctor joined meeting:", user);
        setJoin(true);
        setBothJoined(true);
        if (setDoctorCompleted) setDoctorCompleted(false);
      }

      // Add to participants only if they already have video on
      if (user.bVideoOn) {
        setParticipants((prevParticipants) => {
          if (!prevParticipants.find((p) => p.userId === user.userId)) {
            return [...prevParticipants, user];
          }
          return prevParticipants;
        });
      }
    };

    const handleUserAdded = (payload) => {
      if (Array.isArray(payload)) {
        payload.forEach(processUserAdded);
      } else {
        processUserAdded(payload);
      }
    };

    const processUserRemoved = (payloadObj) => {
      console.log(payloadObj, "Doctor left meeting");
      setParticipants((prevParticipants) =>
        prevParticipants.filter((p) => p.userId !== payloadObj.userId),
      );
      setJoin(false);
    };

    const handleUserRemoved = (payload) => {
      if (Array.isArray(payload)) {
        payload.forEach(processUserRemoved);
      } else {
        processUserRemoved(payload);
      }
    };

    const handleZoomPeer = (payload) => {
      const user = zoomClient.getAllUser().find((u) => u.userId === payload.userId);
      const currentUser = zoomClient.getCurrentUserInfo();

      if (payload.action === "Start") {
        if (!user || (currentUser && payload.userId === currentUser.userId)) return;

        // Render video
        room.attachVideo(payload.userId, 3).then((userVideo) => {
          const container = document.querySelector("video-player-container");
          if (container) {
            container.appendChild(userVideo);
          }
        });

        setParticipants((prevParticipants) => {
          if (!prevParticipants.find((p) => p.userId === payload.userId)) {
            return [...prevParticipants, user];
          }
          return prevParticipants;
        });
        setJoin(true);
        setBothJoined(true);
      } else if (payload.action === "Stop") {
        // Video stopped: remove video/participant.
        room.detachVideo(payload.userId).then(() => {
          const container = document.querySelector("video-player-container");
          if (container) container.innerHTML = "";
        });
        
        setParticipants((prevParticipants) =>
          prevParticipants.filter((p) => p.userId !== payload.userId),
        );

        // When the doctor refreshes or closes the tab, the browser kills
        // the WebRTC connection which fires "Stop", but "user-removed" can
        // be delayed 5-30s while Zoom detects the dead WebSocket.
        // Poll getAllUser() every second to catch the departure as soon as
        // the SDK removes them from the list.
        const departureInterval = setInterval(() => {
          const stillInMeeting = zoomClient
            .getAllUser()
            ?.some((u) => u?.userId === payload.userId);
          if (!stillInMeeting) {
            setJoin(false);
            clearInterval(departureInterval);
          }
        }, 1000);
        // Stop polling after 15 seconds — if user-removed hasn't
        // fired by then, it will handle it on its own.
        setTimeout(() => clearInterval(departureInterval), 15000);
      }
    };

    zoomClient.on("peer-video-state-change", handleZoomPeer);
    zoomClient.on("user-added", handleUserAdded);
    zoomClient.on("user-removed", handleUserRemoved);

    return () => {
      zoomClient.off("peer-video-state-change", handleZoomPeer);
      zoomClient.off("user-added", handleUserAdded);
      zoomClient.off("user-removed", handleUserRemoved);
    };
  }, [room, userId, bookingDetail]);

  useEffect(() => {
    if (zoomClient && room) {
      const currentUser = zoomClient.getCurrentUserInfo();

      zoomClient.getAllUser().forEach(async (user) => {
        if (!user || (currentUser && user.userId === currentUser.userId)) return;

        const isDoctor =
          (bookingDetail?.doctor_id?.user_id && String(user.customId) === String(bookingDetail.doctor_id.user_id)) ||
          (!bookingDetail?.doctor_id?.user_id &&
            user.displayName && user.displayName.trim() !== String(userId).trim() &&
            user.customId !== String(userId));

        if (isDoctor) {
          if (user.bVideoOn) {
            let userVideo = await room.attachVideo(user.userId, 3);
            const container = document.querySelector("video-player-container");
            if (container) container.appendChild(userVideo);
            
            setParticipants((prev) => {
              if (!prev.find((p) => p.userId === user.userId)) {
                return [...prev, user];
              }
              return prev;
            });
          }

          setParticipantAudioState((prev) => ({
            ...prev,
            [user.userId]: user.audio === "computer" || !user.muted,
          }));

          if (user.muted !== undefined) {
            setIsDoctorMuted(user.muted);
          }

          setJoin(true);
          setBothJoined(true);
          setUsers(user);
        }
      });
    }
  }, [zoomClient, room, userId, bookingDetail]);

  // Redundant event listeners removed and consolidated above

  useEffect(() => {
    const handleAudioStateChange = (payload) => {
      console.log("Audio state changed:", payload);
      const currentUser = zoomClient.getCurrentUserInfo();
      if (currentUser && payload.userId !== currentUser.userId) {
        setIsDoctorMuted(payload.action !== "Start");
      }
      setParticipantAudioState((prev) => ({
        ...prev,
        [payload.userId]: payload.action === "Start", // true if audio is on, false if muted
      }));
    };

    zoomClient.on("user-audio-state-change", handleAudioStateChange); // Changed from "peer-audio-state-change"

    const handleUserUpdate = (payload) => {
      console.log("User updated event:", payload);
      const currentUser = zoomClient.getCurrentUserInfo();
      payload.forEach((user) => {
        if (currentUser && user.userId !== currentUser.userId) {
          if (user.muted !== undefined) {
            console.log("Setting isDoctorMuted from user-updated:", user.muted);
            setIsDoctorMuted(user.muted);
          }
        }
      });
    };

    zoomClient.on("user-updated", handleUserUpdate);

    return () => {
      zoomClient.off("user-audio-state-change", handleAudioStateChange);
      zoomClient.off("user-updated", handleUserUpdate);
    };
  }, [zoomClient]);

  async function askPermission() {
    // Checking initial permission state for microphone and camera
    const MicroPermission = await navigator.permissions.query({
      name: "microphone",
    });
    const VideoPermission = await navigator.permissions.query({
      name: "camera",
    });

    // console.log("Micro Permission At Start", MicroPermission);
    // console.log("Video Permission At Start", VideoPermission);

    try {
      // If microphone permission state is "prompt", ask the user for permission
      if (MicroPermission.state === "prompt") {
        Swal.fire({
          title: "Microphone access!",
          text: "Please allow microphone access to communicate.",
          showConfirmButton: false,
          icon: "info",
          allowOutsideClick: false,
        });

        // Request microphone access
        await navigator.mediaDevices.getUserMedia({ audio: true });

        const againMicroPermission = await navigator.permissions.query({
          name: "microphone",
        });
        if (againMicroPermission.state == "granted") {
          room.startAudio();
          setIsAudio(againMicroPermission.state);
          setIsMute(true);
          console.log(
            "Microphone permission granted:",
            againMicroPermission.state,
            isAudio,
          );
        } else if (againMicroPermission.state == "denied") {
          setIsAudio(againMicroPermission.state);
          setIsMute(false);
          console.log(
            "Microphone permission denied:",
            againMicroPermission.state,
            isAudio,
          );
          // Handle the case when permission is denied
          Swal.fire({
            title: "Microphone Access Denied",
            text: "You have denied microphone access. Some features may not work properly.",
            icon: "error",
            confirmButtonText: "Ok",
          });
        }
        Swal.close();
        console.log(
          "Again Microphone Permission Check",
          againMicroPermission,
          isAudio,
        );
      } else {
        setIsAudio(MicroPermission.state);
      }
    } catch (error) {
      // Handle any errors (e.g., if the user denies access)
      console.log("Error requesting microphone permission:", error);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        Swal.fire({
          title: "Microphone Access Denied",
          text: "You have denied microphone access. Some features may not work properly.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    }

    try {
      // If video permission state is "prompt", ask the user for permission
      if (VideoPermission.state === "prompt") {
        Swal.fire({
          title: "Camera access!",
          text: "Please allow camera access to communicate.",
          showConfirmButton: false,
          icon: "info",
          allowOutsideClick: false,
        });

        // Request microphone access
        await navigator.mediaDevices.getUserMedia({ video: true });

        const againVideoPermission = await navigator.permissions.query({
          name: "camera",
        });
        if (againVideoPermission.state == "granted") {
          setIsCamera(againVideoPermission.state);
          setIsVideo(true);
          console.log(
            "Camera permission granted:",
            againVideoPermission.state,
            isCamera,
          );

          room.startVideo().then(() => {
            room
              .attachVideo(zoomClient.getCurrentUserInfo().userId, 3)
              .then((userVideo) => {
                userVideo.muted = true;
                userVideo.autoplay = true;
                userVideo.playsInline = true;
                const container = document.querySelector(
                  "video-local-player-container",
                );
                if (container) {
                  container.appendChild(userVideo);
                  userVideo
                    .play()
                    .catch((error) =>
                      console.error("Error playing video:", error),
                    );
                } else {
                  console.error("Video container not found.");
                }
              });
          });
        } else if (againVideoPermission.state == "denied") {
          setIsCamera(againVideoPermission.state);
          setIsVideo(false);
          console.log(
            "Camera permission denied:",
            againVideoPermission.state,
            isCamera,
          );
          // Handle the case when permission is denied
          Swal.fire({
            title: "Camera Access Denied",
            text: "You have denied microphone access. Some features may not work properly.",
            icon: "warning",
            confirmButtonText: "Ok",
          });
        }
        Swal.close();
        console.log(
          "Again Camera Permission Check",
          againVideoPermission,
          isCamera,
        );
      } else {
        setIsCamera(VideoPermission.state);
      }
    } catch (error) {
      // Handle any errors (e.g., if the user denies access)
      console.log("Error requesting camera permission:", error);
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        Swal.fire({
          title: "Camera Access Denied",
          text: "You have denied camera access. Some features may not work properly.",
          icon: "warning",
          confirmButtonText: "Ok",
        });
      }
    }
  }

  useEffect(() => {
    askPermission();
  }, []);

  useEffect(() => {
    if (initialVideoEnabled) {
      room.startVideo().then(() => {
        room
          .attachVideo(zoomClient.getCurrentUserInfo().userId, 3)
          .then((userVideo) => {
            document
              .querySelector("video-local-player-container")
              .appendChild(userVideo);
          });
      });
      setIsVideo(true);
    }
  }, [initialVideoEnabled]);

  // useEffect(() => {
  //   async function toggleAudio() {
  //     if (initialAudioEnabled) {
  //       const againAudioPermission = await navigator.permissions.query({
  //         name: "microphone",
  //       });
  //       if (againAudioPermission.state == "granted") {
  //         await room.startAudio();
  //         setIsAudio(againAudioPermission.state);
  //         setIsMute(true);
  //       } else if (againAudioPermission.state == "denied") {
  //         setIsAudio(againAudioPermission.state);
  //         setIsMute(false);
  //       }
  //     }
  //   }
  //   toggleAudio();
  // }, [initialAudioEnabled]);

  useEffect(() => {
    async function toggleAudio() {
      const againAudioPermission = await navigator.permissions.query({
        name: "microphone",
      });

      if (againAudioPermission.state === "granted") {
        // Always start audio stream regardless of initialAudioEnabled
        await room.startAudio();
        setIsAudio(againAudioPermission.state);

        // If initialAudioEnabled is false, mute after starting
        if (!initialAudioEnabled) {
          await room.muteAudio();
          setIsMute(false);
        } else {
          setIsMute(true);
        }
      } else if (againAudioPermission.state === "denied") {
        setIsAudio(againAudioPermission.state);
        setIsMute(false);
      }
    }
    toggleAudio();
  }, [initialAudioEnabled]);

  // Deepgram WebSocket connection
  useEffect(() => {
    let cancelled = false;
    let localStream = null;

    const initDeepgram = async () => {
      if (!token) return; // Guard: wait for token to be available
      try {
        // Fetch Deepgram token from backend (never hardcode in frontend)
        const tokenRes = await ApiCall({
          route: "booking/get_deepgram_token",
          token: token,
          verb: "get",
        });
        const deepgramToken = tokenRes?.response?.token;
        if (!deepgramToken) {
          console.error("Failed to retrieve Deepgram token from backend");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        localStream = stream;

        const socket = new WebSocket(
          "wss://api.deepgram.com/v1/listen?model=nova-3&smart_format=true",
          ["token", deepgramToken]
        );

        socket.onopen = () => {
          if (cancelled) {
            socket.close();
            return;
          }
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: "audio/webm",
          });
          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.addEventListener("dataavailable", (event) => {
            if (event.data.size > 0 && socket.readyState === 1) {
              socket.send(event.data);
            }
          });

          mediaRecorder.start(250);
        };

        socket.onmessage = (message) => {
          const received = JSON.parse(message.data);
          const transcript = received.channel?.alternatives[0]?.transcript;
          if (transcript && received.is_final) {
            // Only display/send if mic is unmuted (isMute === true means unmuted)
            if (!isMuteRef.current) return;

            // Local Echo: Add to history as Patient
            setCaptionHistory((prev) => [
              ...prev,
              { speaker: "Patient", text: transcript, timestamp: Date.now() },
            ]);

            // Log local transcript
            transcriptLogRef.current.push({
              speaker: "patient",
              text: transcript,
              timestamp: Date.now(),
            });

            // Sync to backend
            syncTranscriptToBackend(transcriptLogRef.current);

            // Send to doctor via Zoom Command Channel
            const cmdClient = zoomClient.getCommandClient();
            if (cmdClient) {
              cmdClient.send(
                JSON.stringify({ text: transcript, speaker: "patient" })
              );
            }
          }
        };

        socket.onerror = (error) => {
          console.error("Deepgram error:", error);
        };

        socketRef.current = socket;
      } catch (err) {
        console.error("Error setting up deepgram", err);
      }
    };

    initDeepgram();

    // Listen for doctor's transcripts
    const handleCommandMessage = (payload) => {
      try {
        const data = JSON.parse(payload.text);
        if (data.text && data.speaker !== "patient") {
          // Add remote caption as Doctor
          setCaptionHistory((prev) => [
            ...prev,
            {
              speaker: "Doctor",
              text: data.text,
              timestamp: Date.now(),
            },
          ]);
          transcriptLogRef.current.push({
            speaker: data.speaker || "remote", text: data.text, timestamp: Date.now(),
          });
          syncTranscriptToBackend(transcriptLogRef.current);
        }
      } catch (e) {
        console.error("Error parsing command message:", e);
      }
    };

    zoomClient.on("command-channel-message", handleCommandMessage);

    return () => {
      cancelled = true;
      if (socketRef.current) socketRef.current.close();
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
      if (localStream) localStream.getTracks().forEach((t) => t.stop());
      if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
      zoomClient.off("command-channel-message", handleCommandMessage);
    };
  }, []);

  useEffect(() => {
    const handleDoctorCompleted = (data) => {
      console.log("doctor left the video");
      console.log(data?.booking_id, roomName, data?.booking_id === roomName);
      if (data?.booking_id === roomName) setDoctorCompleted(true);
    };

    socketConnection.on("doctorCompleted", handleDoctorCompleted);
    return () => {
      socketConnection.off("doctorCompleted", handleDoctorCompleted);
    };
  }, [roomName]); // Added roomName as dependency for safety

  // on metting leave
  const onMeetingLeave = async (id) => {
    try {
      dispatch(setLoader(true));

      const updatedBookingDetails = await getBookingDetails();

      // console.log("updatedBookingDetails", updatedBookingDetails);

      if (
        !updatedBookingDetails?.doctor_id?.user_id ||
        updatedBookingDetails?.booking_status === "upcoming"
      ) {
        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to report delay from doctor and reschedule?",
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: "No",
          confirmButtonText: "Yes",
          confirmButtonColor: "#c82333",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              setShowReportModal(true);
            } catch (error) {
              console.error("Error showing report modal:", error);
            }
          }
        });
        return;
      } else {
        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to end consultation?",
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: "No",
          confirmButtonText: "Yes",
          confirmButtonColor: "#c82333",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              handleLogout(true);
            } catch (error) {
              console.error("Error during logout:", error);
            }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching updated booking details:", error);
    } finally {
      dispatch(setLoader(false));
    }
  };

  const sendNotificationHandler = async () => {
    try {
      dispatch(setLoader(true));

      if (!bookingDetail?._id) {
        console.error("Missing booking detail ID for notification");
        dispatch(setLoader(false));
        return;
      }

      const res = await ApiCall({
        route: `receiptionist/add_notifications_for_receptionists`,
        token: token,
        verb: "post",
        params: {
          bookingId: bookingDetail?._id,
        },
      });
      // console.log(res?.response, "response");
      if (res?.status == "200") {
        // console.log("res", res?.response);
        dispatch(setLoader(false));

        Swal.fire(
          "Your request for urgent attention has been notified.",
          "Staff will be in contact with you soon.",
          "success",
        );
        setRingTime(new Date());
        setDelayTime(new Date());
      } else {
        console.log("your error is there", res?.response);
        dispatch(setLoader(false));
      }
    } catch (err) {
      console.log("Coudln't send", err);
      dispatch(setLoader(false));
    }
  };

  const reportAndRescheduleHandler = async () => {
    setShowReportModal(true);
    setDelayTime(new Date());
    setRingTime(new Date());
  };

  const closeReportModal = () => {
    setShowReportModal(false);
  };

  // Reset timers whenever a participant joins or leaves
  useEffect(() => {
    const now = new Date();
    setRingTime(now);
    if (delayTime) setDelayTime(now);
    setCountdown({ minutes: 0, seconds: 0 });
    setTimerMessage("");
    setTimerType("");
  }, [participants?.length > 0]);

  // Timer update effect
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const minutesPassedRing = (now - ringTime) / (1000 * 60);
      const minutesPassedDelay = delayTime
        ? (now - delayTime) / (1000 * 60)
        : 0;

      const hasDoctorNotJoined =
        participants?.length <= 0 && bookingDetail?.doctor_id?.user_id;
      const hasNoDoctor =
        participants?.length <= 0 && !bookingDetail?.doctor_id?.user_id;

      if (doctorCompleted) {
        setCountdown({ minutes: 0, seconds: 0 });
        setTimerMessage("");
        setTimerType("");
        return;
      }

      if (hasNoDoctor) {
        // Check if delay timer has been started (Ring Again was clicked)
        if (delayTime && minutesPassedDelay >= 20) {
          // Show report button after 20 minutes from Ring Again click
          setCountdown({ minutes: 0, seconds: 0 });
          setTimerMessage("");
          setTimerType("delay");
        } else if (delayTime && minutesPassedDelay < 20) {
          // Countdown to report delay (20min - time passed since Ring Again)
          const totalSeconds = Math.max(
            0,
            Math.floor(20 * 60 - minutesPassedDelay * 60),
          );
          const mins = Math.floor(totalSeconds / 60);
          const secs = totalSeconds % 60;
          setCountdown({ minutes: mins, seconds: secs });
          setTimerMessage(
            `If no doctor joins in ${formatCountdown(
              mins,
              secs,
            )}, you can report the delay and reschedule your appointment.`,
          );
          setTimerType("delay");
        } else if (minutesPassedRing >= 10) {
          // Show Ring Again button (enabled, no countdown)
          setCountdown({ minutes: 0, seconds: 0 });
          setTimerMessage("");
          setTimerType("ring");
        } else {
          // Countdown to ring again (10min - time passed)
          const totalSeconds = Math.max(
            0,
            Math.floor(10 * 60 - minutesPassedRing * 60),
          );
          const mins = Math.floor(totalSeconds / 60);
          const secs = totalSeconds % 60;
          setCountdown({ minutes: mins, seconds: secs });
          setTimerMessage(
            `If no doctor joins in ${formatCountdown(
              mins,
              secs,
            )}, you can ring again to alert the staff.`,
          );
          setTimerType("ring");
        }
      } else if (hasDoctorNotJoined) {
        if (minutesPassedRing >= 10) {
          setCountdown({ minutes: 0, seconds: 0 });
          setTimerMessage("");
          setTimerType("remind");
        } else {
          const totalSeconds = Math.max(
            0,
            Math.floor(10 * 60 - minutesPassedRing * 60),
          );
          const mins = Math.floor(totalSeconds / 60);
          const secs = totalSeconds % 60;
          setCountdown({ minutes: mins, seconds: secs });
          setTimerMessage(
            `If your assigned doctor doesn't join in ${formatCountdown(
              mins,
              secs,
            )}, you can send them a reminder.`,
          );
          setTimerType("remind");
        }
      } else {
        setCountdown({ minutes: 0, seconds: 0 });
        setTimerMessage("");
        setTimerType("");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [ringTime, delayTime, participants?.length === 0, bookingDetail]);

  // Handler for reminding assigned doctor
  const remindDoctorHandler = async () => {
    try {
      dispatch(setLoader(true));

      if (!bookingDetail?._id) {
        console.error("Missing booking detail ID for reminder");
        dispatch(setLoader(false));
        return;
      }

      const res = await ApiCall({
        route: `doctor/add_notifications_for_doctor`,
        token: token,
        verb: "post",
        params: {
          bookingId: bookingDetail?._id,
        },
      });

      if (res?.status == "200") {
        dispatch(setLoader(false));

        Swal.fire(
          "Doctor Reminded",
          "A reminder has been sent to your assigned doctor.",
          "success",
        );
        setRingTime(new Date());
      } else {
        dispatch(setLoader(false));
        console.log("Error reminding doctor", res?.response);
      }
    } catch (err) {
      dispatch(setLoader(false));
      console.log("Couldn't send reminder", err);
    }
  };

  // let items = [];

  // if (
  //   participants?.length <= 0 &&
  //   !bookingDetail?.doctor_id?.user_id &&
  //   (new Date() - timePassedForDelay) / (1000 * 60) > 4
  // ) {
  //   items.push({
  //     id: "1",
  //     label: (
  //       <PrimaryButton
  //         className="report-button "
  //         onClick={reportAndRescheduleHandler}
  //       >
  //         Report Delay & Reschedule
  //       </PrimaryButton>
  //     ),
  //   });
  // }

  // if (
  //   participants?.length <= 0 &&
  //   !bookingDetail?.doctor_id?.user_id &&
  //   (new Date() - timePassed) / (1000 * 60) > 2
  // ) {
  //   items.push({
  //     id: "2",
  //     label: (
  //       <PrimaryButton
  //         className="notify-receptionist"
  //         onClick={sendNotificationHandler}
  //         icon={
  //           <i
  //             style={{ transform: `rotate(0deg)` }}
  //             class="fa-solid fa-phone"
  //           ></i>
  //         }
  //       >
  //         Ring again
  //       </PrimaryButton>
  //     ),
  //   });
  // }

  // Determine button state
  const getButtonConfig = () => {
    const now = new Date();
    const minutesPassedRing = (now - ringTime) / (1000 * 60);
    const minutesPassedDelay = delayTime ? (now - delayTime) / (1000 * 60) : 0;

    if (doctorCompleted) {
      return null;
    }

    const hasNoDoctor =
      participants?.length <= 0 && !bookingDetail?.doctor_id?.user_id;
    const hasDoctorNotJoined =
      participants?.length <= 0 && bookingDetail?.doctor_id?.user_id && !join;

    if (hasNoDoctor) {
      // Delay button (after Ring Again was clicked and 20 min passed)
      if (delayTime && minutesPassedDelay >= 20) {
        return {
          type: "report",
          enabled: true,
          label: "Report Delay & Reschedule",
          onClick: reportAndRescheduleHandler,
          icon: <i className="fa-solid fa-exclamation-triangle"></i>,
          className: "report-button",
        };
      }
      // Delay countdown (after Ring Again was clicked, counting down to 20 min)
      else if (delayTime && minutesPassedDelay < 20) {
        return {
          type: "report",
          enabled: false,
          label: `Report Delay (${formatCountdown(
            countdown.minutes,
            countdown.seconds,
          )})`,
          icon: <i className="fa-solid fa-exclamation-triangle"></i>,
          className: "report-button",
        };
      }
      // Ring Again enabled (after 10 min but before clicking Ring Again)
      else if (!delayTime && minutesPassedRing >= 10) {
        return {
          type: "ring",
          enabled: true,
          label: "Ring Again",
          onClick: sendNotificationHandler,
          icon: (
            <i
              style={{ transform: `rotate(0deg)` }}
              className="fa-solid fa-phone"
            ></i>
          ),
          className: "notify-receptionist",
        };
      }
      // Ring Again countdown (first 10 minutes)
      else if (!delayTime && minutesPassedRing < 10) {
        return {
          type: "ring",
          enabled: false,
          label: `Ring Again (${formatCountdown(
            countdown.minutes,
            countdown.seconds,
          )})`,
          onClick: sendNotificationHandler,
          icon: (
            <i
              style={{ transform: `rotate(0deg)` }}
              className="fa-solid fa-phone"
            ></i>
          ),
          className: "notify-receptionist",
        };
      }
    } else if (hasDoctorNotJoined) {
      if (minutesPassedRing >= 10) {
        return {
          type: "remind",
          enabled: true,
          label: "Remind Doctor",
          onClick: remindDoctorHandler,
          icon: <i className="fa-solid fa-bell"></i>,
          className: "remind-doctor",
        };
      } else {
        return {
          type: "remind",
          enabled: false,
          label: `Remind Doctor (${formatCountdown(
            countdown.minutes,
            countdown.seconds,
          )})`,
          icon: <i className="fa-solid fa-bell"></i>,
          className: "remind-doctor",
        };
      }
    }

    return null;
  };

  const buttonConfig = getButtonConfig();

  // Render action button
  const ActionButton = buttonConfig ? (
    <PrimaryButton
      className={buttonConfig.className}
      onClick={buttonConfig.onClick}
      disabled={!buttonConfig.enabled}
      icon={buttonConfig.icon}
    >
      {buttonConfig.label}
    </PrimaryButton>
  ) : null;

  const toggleVideo = async () => {
    // console.log("Valuevale", isVideo);
    if (isCamera === "granted") {
      if (isVideo) {
        // console.log("video is allowed", isVideo);
        room.stopVideo().then(() => {
          room
            .detachVideo(zoomClient.getCurrentUserInfo().userId)
            .then((userVideo) => {
              document.querySelector("video-local-player-container").innerHTML =
                "";
            });
        });
        setIsVideo(false);
      } else {
        room.startVideo().then(() => {
          room
            .attachVideo(zoomClient.getCurrentUserInfo().userId, 3)
            .then((userVideo) => {
              // console.log("userVideo", userVideo);
              document
                .querySelector("video-local-player-container")
                .appendChild(userVideo);
            });
        });
        setIsVideo(true);
      }
    } else {
      Swal.fire({
        title: "Camera Permission",
        text: "You have denied camera access. Please allow from the setting.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  const toggleAudio = async () => {
    try {
      // console.log("ismute value is", isMute);
      if (isAudio === "granted") {
        await room.startAudio();
        if (isMute) {
          await room.muteAudio();
          setIsMute(false);
        } else {
          await room.unmuteAudio();
          setIsMute(true);
        }
      } else {
        Swal.fire({
          title: "Microphone Access Denied",
          text: "You have denied microphone access. Please allow from the setting.",
          icon: "warning",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Error toggling audio:", error);
    }
  };

  // console.log("????????", participants);

  return (
    <>
      <div className="main-container-video-chat">
        <div className="video-chat-container-header">
          {!isMobile && (
            <DrawerApp
              getBookingDetails={getBookingDetails}
              bookingDetail={bookingDetail}
            />
          )}
          <img
            onClick={() => navigate("/my-consultation")}
            src={logo1}
            alt="logo"
            className="video-chat-header-logo"
          />

          <button
            className="custom-button-video-call mobile-end-consultation end-consultation"
            onClick={() => onMeetingLeave(bookingDetail?._id)}
          >
            End
          </button>
        </div>

        {isMobile && <div className="header-spacer"></div>}

        <div className="video-chat-section-container-main">
          <div className="video-chat-inner-contianer">
            <div className="videoDiv-main">
              <div
                className={`doctor-mic-indicator-wrapper ${isDoctorMuted ? "mic-muted" : "mic-active"}`}
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "15px",
                  zIndex: 1000,
                  width: "fit-content",
                  display: bothJoined && join ? "flex" : "none",
                }}
              >
                <i
                  className={`fa-solid fa-microphone${isDoctorMuted ? "-slash" : ""} icon`}
                ></i>
                <span className="text">
                  {isDoctorMuted ? "Doctor Muted" : "Doctor's mic is on"}
                </span>
              </div>

              <div
                className="remote-video"
                style={{
                  display: participants.length > 0 ? "block" : "none",
                }}
              >
                <video-player-container></video-player-container>
              </div>

              <div
                className="video-call-ended-text-in-box"
                style={{
                  display: participants.length === 0 ? "block" : "none",
                }}
              >
                <h4 className="h4">
                  {join
                    ? "Doctor has turned off his camera!"
                    : doctorCompleted
                      ? "Consultation Completed!"
                      : bothJoined
                        ? "Doctor has left the meeting"
                        : timerType === "remind" &&
                          countdown.minutes === 0 &&
                          countdown.seconds === 0
                          ? "Still waiting for your assigned doctor?"
                          : timerType === "delay" &&
                            countdown.minutes === 0 &&
                            countdown.seconds === 0
                            ? "We apologize for the extended wait"
                            : timerType === "ring" &&
                              countdown.minutes === 0 &&
                              countdown.seconds === 0
                              ? "Still waiting?"
                              : "Your Consultation Is in Queue!"}
                </h4>

                {bothJoined &&
                  !join &&
                  doctorCompleted && (
                    <p>
                      Your consultation is complete. Please open the
                      consultation details to view any prescriptions
                      recommended by your doctor.
                    </p>
                  )}

                {!doctorCompleted &&
                  !bothJoined && (
                    <>
                      {/* Default waiting message */}
                      {!timerType && (
                        <p>
                          Hang tight. We've notified all available doctors.
                          Your video visit is in the queue and will start as
                          soon as a doctor joins. Feel free to ask any
                          questions in the chat while you wait.
                        </p>
                      )}

                      {/* Ring Again countdown message */}
                      {timerType === "ring" &&
                        (countdown.minutes > 0 || countdown.seconds > 0) && (
                          <>
                            <p>
                              Hang tight. We've notified all available
                              doctors. Your video visit is in the queue and
                              will start as soon as a doctor joins. Feel free
                              to ask any questions in the chat while you wait.
                            </p>
                            <p className="mt-4">
                              <i className="fa-solid fa-clock"></i> If no
                              doctor joins in{" "}
                              {formatCountdown(
                                countdown.minutes,
                                countdown.seconds,
                              )}
                              , you can ring again to alert the staff.
                            </p>
                          </>
                        )}

                      {/* Ring Again ready message */}
                      {timerType === "ring" &&
                        countdown.minutes === 0 &&
                        countdown.seconds === 0 && (
                          <p>
                            Still waiting? You can notify all available
                            doctors again. Press "Ring Again" to send a new
                            alert.
                          </p>
                        )}

                      {/* Delay countdown message */}
                      {timerType === "delay" &&
                        (countdown.minutes > 0 || countdown.seconds > 0) && (
                          <>
                            <p>
                              Hang tight. We've again notified all available
                              doctors. Your video visit is in the queue and
                              will start as soon as a doctor joins. Feel free
                              to ask any questions in the chat while you wait.
                            </p>
                            <p className="mt-4">
                              <i className="fa-solid fa-clock"></i> If no
                              doctor joins in{" "}
                              {formatCountdown(
                                countdown.minutes,
                                countdown.seconds,
                              )}
                              , you can report the delay and reschedule your
                              appointment.
                            </p>
                          </>
                        )}

                      {/* Delay ready message */}
                      {timerType === "delay" &&
                        countdown.minutes === 0 &&
                        countdown.seconds === 0 && (
                          <p>
                            We apologize for the extended wait. You can now
                            report this delay and reschedule your appointment
                            to a more convenient time.
                          </p>
                        )}

                      {/* Remind Doctor countdown message */}
                      {timerType === "remind" &&
                        (countdown.minutes > 0 || countdown.seconds > 0) && (
                          <>
                            <p>
                              Hang tight. Your doctor has been assigned. Your
                              video visit is in the queue and will start as
                              soon as a doctor joins. Feel free to ask any
                              questions in the chat while you wait.
                            </p>
                            <p className="mt-4">
                              <i className="fa-solid fa-clock"></i> If they
                              don't join in{" "}
                              {formatCountdown(
                                countdown.minutes,
                                countdown.seconds,
                              )}
                              , you can send them a reminder.
                            </p>
                          </>
                        )}

                      {/* Remind Doctor ready message */}
                      {timerType === "remind" &&
                        countdown.minutes === 0 &&
                        countdown.seconds === 0 && (
                          <p>
                            Still waiting for your assigned doctor? You can
                            send them a direct reminder by pressing "Remind
                            Doctor" below.
                          </p>
                        )}
                    </>
                  )}
              </div>

              <div className="local-user-video">
                {isCamera === "granted" ? (
                  isVideo ? (
                    <div className="local-video">
                      <video-player-container>
                        <video-local-player-container></video-local-player-container>
                      </video-player-container>
                    </div>
                  ) : (
                    <div className="local-user-off-cam">
                      <i class={`fa-solid fa-video-slash text-7`}></i>
                      <p>Camera turned off.</p>
                    </div>
                  )
                ) : (
                  ""
                )}

                <video-local-player-container></video-local-player-container>
              </div>

              {showCaptions && captionHistory.length > 0 && (
                <div className="zoom-caption-history-container">
                  <div className="caption-history-list">
                    {captionHistory.map((item, index) => (
                      <div
                        key={index}
                        className="caption-item"
                      >
                        <span
                          className={`caption-speaker ${item.speaker === "Doctor" ? "doctor" : "patient"}`}
                        >
                          {item.speaker}:
                        </span>
                        <span className="caption-text">{item.text}</span>
                      </div>
                    ))}
                    <div ref={captionEndRef} />
                  </div>
                </div>
              )}

              {isMobile && (
                <div className="mobile-long-buttons-on-top">
                  {/* {items?.length === 2
                    ? items?.find((el) => el?.id === "1")?.label
                    : items?.find((el) => el?.id === "2")?.label} */}

                  {participants?.length <= 0 && !join && ActionButton}
                </div>
              )}

              {/* JSX for mobile */}
              {/* {isMobile && participants?.length <= 0 && (
                <>
                  {timerMessage && (
                    <div className="timer-message-mobile">
                      <i className="fa-solid fa-clock"></i> {timerMessage}
                    </div>
                  )}
                  <div className="mobile-long-buttons-on-top">
                    {ActionButton}
                  </div>
                </>
              )} */}

              <div className="video-chat-controls">
                {isMobile && (
                  <DrawerApp
                    getBookingDetails={getBookingDetails}
                    bookingDetail={bookingDetail}
                  />
                )}
                {isMobile && (
                  <ChatDrawer
                    selectedChat={{
                      _id: bookingDetail?.chat_id,
                      booking: bookingDetail,
                    }}
                  />
                )}

                <button
                  className={
                    isCamera === "granted"
                      ? "chat-control-button video-active"
                      : "chat-control-button"
                  }
                  onClick={() => toggleVideo()}
                  style={{
                    backgroundColor:
                      isCamera === "granted"
                        ? isVideo
                          ? "blue"
                          : "red"
                        : "orange",
                  }}
                  title={
                    isCamera == "granted"
                      ? "Video Management"
                      : "Please provide video permissions"
                  }
                >
                  <i
                    class={`fa-solid fa-video${isVideo ? "" : "-slash"} text-7`}
                  ></i>
                </button>

                <button
                  className={
                    isAudio === "granted"
                      ? "chat-control-button video-active"
                      : "chat-control-button"
                  }
                  onClick={() => toggleAudio()}
                  style={{
                    backgroundColor:
                      isAudio === "granted"
                        ? isMute
                          ? "blue"
                          : "red"
                        : "orange",
                  }}
                  title={
                    isMute
                      ? "Audio Management"
                      : "Please provide audio permissions"
                  }
                >
                  <i
                    class={`fa-solid fa-microphone${isMute ? "" : "-slash"
                      } text-3`}
                  ></i>
                </button>
                <button
                  className={`chat-control-button ${showCaptions ? "captions-active" : ""}`}
                  onClick={() => setShowCaptions(!showCaptions)}
                  title={showCaptions ? "Hide Captions" : "Show Captions"}
                  style={{
                    backgroundColor: showCaptions ? "blue" : "red"
                  }}
                >
                  <i className="fa-solid fa-closed-captioning"></i>
                </button>
              </div>
              {/* <div className="video-chat-main-buttons">
                <PrimaryButton
                  className="end-consultation"
                  onClick={() => onMeetingLeave(bookingDetail?._id)}
                >
                  End Consultation
                  
                </PrimaryButton>
                {items?.length === 2
                  ? items?.find((el) => el?.id === "1")?.label
                  : items?.find((el) => el?.id === "2")?.label}
              </div> */}

              {/* {!isMobile && timerMessage && participants?.length <= 0 && (
                <div className="timer-message-desktop">
                  <i className="fa-solid fa-clock"></i> {timerMessage}
                </div>
              )} */}

              <div className="video-chat-main-buttons">
                <PrimaryButton
                  className="end-consultation"
                  onClick={() => onMeetingLeave(bookingDetail?._id)}
                >
                  End Consultation
                </PrimaryButton>
                {participants?.length <= 0 && !join && ActionButton}
              </div>
            </div>
          </div>

          {bookingDetail && !isMobile && (
            <ChatBox
              selectedChat={{
                _id: bookingDetail?.chat_id,
                booking: bookingDetail,
              }}
            />
          )}
        </div>
      </div>
      <ReportModal
        open={showReportModal}
        close={closeReportModal}
        bookingId={bookingDetail?._id}
        handleLogout={handleLogout}
      />
    </>
  );
};

export default Room;

// {participants.length > 0 &&
//                     participants.map((participant) => (
//                       <span
//                         key={participant.userId}
//                         style={{ position: "relative" }}
//                       >

//                         <div
//                       style={{
//                         position: "absolute",
//                         bottom: "10px",
//                         left: "10px",
//                         zIndex: 10,
//                       }}
//                     >
//                         {participantAudioState[participant.userId] ? (
//                           <span>🎤</span> // Mic on icon
//                         ) : (
//                           <span>🔇</span> // Mic muted icon
//                         )}
//                         </div>
//                       </span>
//                     ))}
